// intentTypes — stabil enum + typer för intent-klassificering.
//
// Varje user-prompt klassificeras som exakt en intent. Intent:n avgör
// hur AI-svaret hanteras downstream:
//   - build_new / extend / modify_in_place / refactor / revert produces
//     artifact-versioner (Batch DDD)
//   - explain → inget artefakt, bara chat
//   - meta.* → skippar content-guards (Batch BBB) och AI-call för vissa
//
// Källor för klassificering:
//   manual  = user valde explicit i dropdown
//   keyword = regel-baserad matchning mot prompt-innehåll
//   llm     = fallback-LLM-call när keyword är tvetydig
// (beställarens svar på öppen fråga 6: dropdown är manuell override.)

export const INTENTS = [
  'build_new',
  'extend',
  'modify_in_place',
  'refactor',
  'revert',
  'explain',
  'meta.moderation',
  'meta.history',
] as const;

export type Intent = (typeof INTENTS)[number];

export type IntentSource = 'manual' | 'keyword' | 'llm';

export interface IntentClassification {
  intent: Intent;
  confidence: number; // 0-1
  source: IntentSource;
  reasoning?: string;
  matchedPattern?: string;
}

/** Intents som producerar en ny artifact-version. */
export const ARTIFACT_INTENTS: readonly Intent[] = [
  'build_new', 'extend', 'modify_in_place', 'refactor', 'revert',
];

/** Intents som inte rör canvas / artefakter. */
export const NON_ARTIFACT_INTENTS: readonly Intent[] = [
  'explain', 'meta.moderation', 'meta.history',
];

/** Human-readable label för dropdown + badges. */
export const INTENT_LABELS: Record<Intent, { sv: string; en: string; hint?: string }> = {
  build_new: { sv: 'Bygg nytt', en: 'Build new', hint: 'Skapa ett nytt flöde från scratch' },
  extend: { sv: 'Bygg vidare', en: 'Extend', hint: 'Lägg till ovanpå befintligt — bevarar existerande' },
  modify_in_place: { sv: 'Ändra', en: 'Modify', hint: 'Ändra befintlig del (kan vara destructive)' },
  refactor: { sv: 'Refaktorera', en: 'Refactor', hint: 'Omstrukturera utan beteendeändring' },
  revert: { sv: 'Återgå', en: 'Revert', hint: 'Gå tillbaka till tidigare version' },
  explain: { sv: 'Förklara', en: 'Explain', hint: 'Förklara utan att ändra canvas' },
  'meta.moderation': { sv: 'Fråga om moderation', en: 'Moderation question', hint: 'Fråga om varningar/strikes' },
  'meta.history': { sv: 'Fråga om historik', en: 'History question', hint: 'Fråga om egna chattar/flöden' },
};

/** Default-intent när ingen klassificering kunnat göras. */
export const DEFAULT_INTENT: Intent = 'build_new';

/** Confidence-threshold under vilket UI visar bekräftelse. */
export const INTENT_CONFIDENCE_CONFIRM_THRESHOLD = 0.7;

/** Confidence under vilket keyword-klassificerare ska eskalera till LLM-fallback. */
export const KEYWORD_FALLBACK_THRESHOLD = 0.6;
